import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  Loader2,
  MoreVertical,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { DatePickerButton } from '@/components/ui/date-picker-button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

type ApiFreeGiftCostRow = {
  id: number;
  product_id: number | null;
  sku: string;
  main_title: string | null;
  unit_cost: string;
  effective_from: string;
  effective_to: string | null;
  created_at: string | null;
  updated_at: string | null;
};

type ProductOption = {
  id: number;
  sku: string;
  main_title: string | null;
  variant_name: string | null;
  brand_name: string | null;
};

function formatDateTime(value: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatCost(value: string) {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toFixed(2);
}

export function FreeGiftCostPage() {
  const [rows, setRows] = useState<ApiFreeGiftCostRow[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<ApiFreeGiftCostRow | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductOption | null>(null);
  const [skuPickerOpen, setSkuPickerOpen] = useState(false);
  const [skuQuery, setSkuQuery] = useState('');
  const [skuOptions, setSkuOptions] = useState<ProductOption[]>([]);
  const [skuLoading, setSkuLoading] = useState(false);
  const [unitCost, setUnitCost] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<ApiFreeGiftCostRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const haystack = `${row.sku} ${row.product_id ?? ''} ${row.main_title ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, searchQuery]);

  async function loadRows() {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch('/v1/api/gp-reporter/free-gift-costs/', { method: 'GET' });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }

      const data = (await resp.json()) as unknown;
      const arr = Array.isArray(data) ? data : [];
      const parsed = arr
        .map((x) => {
          const id = Number((x as any)?.id);
          const sku = String((x as any)?.sku ?? '').trim();
          if (!Number.isFinite(id) || !sku) return null;
          const productIdRaw = (x as any)?.product_id;
          const product_id =
            productIdRaw === null || productIdRaw === undefined ? null : Number(productIdRaw);
          return {
            id,
            product_id: Number.isFinite(product_id as number) ? (product_id as number) : null,
            sku,
            main_title: (x as any)?.main_title ? String((x as any).main_title) : null,
            unit_cost: String((x as any)?.unit_cost ?? ''),
            effective_from: String((x as any)?.effective_from ?? ''),
            effective_to: (x as any)?.effective_to ? String((x as any).effective_to) : null,
            created_at: (x as any)?.created_at ? String((x as any).created_at) : null,
            updated_at: (x as any)?.updated_at ? String((x as any).updated_at) : null,
          } satisfies ApiFreeGiftCostRow;
        })
        .filter(Boolean) as ApiFreeGiftCostRow[];

      setRows(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load free gift cost rows.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadRows();
  }, []);

  useEffect(() => {
    if (!dialogOpen) return;

    const q = skuQuery.trim();
    if (!q) {
      setSkuOptions(selectedProduct ? [selectedProduct] : []);
      setSkuLoading(false);
      return;
    }

    const timeout = window.setTimeout(async () => {
      setSkuLoading(true);
      try {
        const params = new URLSearchParams({
          q,
          page: '1',
          page_size: '20',
        });
        const resp = await fetch(`/v1/api/gp-reporter/products/management/?${params.toString()}`, {
          method: 'GET',
        });
        if (!resp.ok) {
          const text = await resp.text().catch(() => '');
          throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
        }

        const data = (await resp.json()) as any;
        const arr = Array.isArray(data?.results) ? data.results : [];
        const parsed = arr
          .map((x: any) => {
            const id = Number(x?.id);
            const sku = String(x?.sku ?? '').trim();
            if (!Number.isFinite(id) || !sku) return null;
            return {
              id,
              sku,
              main_title: x?.main_title ? String(x.main_title) : null,
              variant_name: x?.variant_name ? String(x.variant_name) : null,
              brand_name: x?.brand_name ? String(x.brand_name) : null,
            } satisfies ProductOption;
          })
          .filter(Boolean) as ProductOption[];
        setSkuOptions(parsed);
      } catch (e) {
        setSaveError(e instanceof Error ? e.message : 'Failed to search SKU options.');
        setSkuOptions([]);
      } finally {
        setSkuLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [dialogOpen, skuQuery, selectedProduct]);

  function resetDialogState() {
    setEditingRow(null);
    setSelectedProduct(null);
    setSkuPickerOpen(false);
    setSkuQuery('');
    setSkuOptions([]);
    setUnitCost('');
    setEffectiveFrom('');
    setEffectiveTo('');
    setSaveError(null);
  }

  function openCreateDialog() {
    resetDialogState();
    setDialogOpen(true);
  }

  function openEditDialog(row: ApiFreeGiftCostRow) {
    setEditingRow(row);
    setSelectedProduct({
      id: row.product_id ?? 0,
      sku: row.sku,
      main_title: row.main_title,
      variant_name: null,
      brand_name: null,
    });
    setSkuQuery(row.sku);
    setSkuOptions([
      {
        id: row.product_id ?? 0,
        sku: row.sku,
        main_title: row.main_title,
        variant_name: null,
        brand_name: null,
      },
    ]);
    setUnitCost(row.unit_cost);
    setEffectiveFrom(row.effective_from);
    setEffectiveTo(row.effective_to ?? '');
    setSaveError(null);
    setDialogOpen(true);
  }

  async function saveRow() {
    if (!selectedProduct || !selectedProduct.sku) {
      setSaveError('Please select a matching SKU from the dropdown.');
      return;
    }
    if (!unitCost.trim()) {
      setSaveError('Please enter a unit cost.');
      return;
    }
    if (!effectiveFrom) {
      setSaveError('Please select an effective from date.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        product_id: selectedProduct.id > 0 ? selectedProduct.id : undefined,
        sku: selectedProduct.sku,
        unit_cost: unitCost.trim(),
        effective_from: effectiveFrom,
        effective_to: effectiveTo || null,
      };

      const url = editingRow
        ? `/v1/api/gp-reporter/free-gift-costs/${editingRow.id}/`
        : '/v1/api/gp-reporter/free-gift-costs/';
      const method = editingRow ? 'PATCH' : 'POST';

      const resp = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }

      await loadRows();
      setDialogOpen(false);
      resetDialogState();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save free gift cost row.');
    } finally {
      setSaving(false);
    }
  }

  function openDeleteDialog(row: ApiFreeGiftCostRow) {
    setDeleteRow(row);
    setDeleteError(null);
    setDeleteOpen(true);
  }

  async function deleteSelectedRow() {
    if (!deleteRow || deleting) return;

    setDeleting(true);
    setDeleteError(null);
    try {
      const resp = await fetch(`/v1/api/gp-reporter/free-gift-costs/${deleteRow.id}/`, {
        method: 'DELETE',
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }

      setRows((current) => current.filter((row) => row.id !== deleteRow.id));
      setDeleteOpen(false);
      setDeleteRow(null);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete free gift cost row.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search SKU / title / product id..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button variant="outline" onClick={loadRows} disabled={loading} className="gap-2">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Refresh
        </Button>
        <Button onClick={openCreateDialog} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Cost
        </Button>
      </div>

      <div className="text-sm text-muted-foreground">
        Showing {filteredRows.length} of {rows.length} free gift cost rows
      </div>

      {error && (
        <div className="flex items-start gap-2 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4" />
          <span className="break-words">{error}</span>
        </div>
      )}

      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">SKU</TableHead>
              <TableHead>Product</TableHead>
              <TableHead className="w-[120px] text-right">Unit Cost</TableHead>
              <TableHead className="w-[140px]">Effective From</TableHead>
              <TableHead className="w-[140px]">Effective To</TableHead>
              <TableHead className="w-[180px]">Updated At</TableHead>
              <TableHead className="w-[90px] text-center">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.length > 0 ? (
              filteredRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-sm">{row.sku}</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-sm">{row.main_title || '—'}</div>
                      <div className="text-xs text-muted-foreground">product_id: {row.product_id ?? '—'}</div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono">{formatCost(row.unit_cost)}</TableCell>
                  <TableCell>{row.effective_from}</TableCell>
                  <TableCell>{row.effective_to ?? 'Open ended'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDateTime(row.updated_at)}</TableCell>
                  <TableCell className="text-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => openEditDialog(row)}>Edit</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={() => openDeleteDialog(row)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                  {loading ? 'Loading free gift costs…' : 'No free gift cost rows found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetDialogState();
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingRow ? 'Edit free gift cost' : 'Add free gift cost'}</DialogTitle>
            <DialogDescription>
              {editingRow ? (
                <>
                  Editing row id <span className="font-mono">{editingRow.id}</span>
                </>
              ) : (
                'Create a new date-bounded free gift cost row.'
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label>SKU</Label>
              <Popover open={skuPickerOpen} onOpenChange={setSkuPickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={skuPickerOpen} className="w-full justify-between">
                    <span className="truncate">
                      {selectedProduct ? selectedProduct.sku : 'Search and select a SKU'}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[420px] max-w-[80vw] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Type SKU or title..."
                      value={skuQuery}
                      onValueChange={(value) => setSkuQuery(value)}
                    />
                    <CommandList>
                      {skuLoading ? (
                        <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Searching products…
                        </div>
                      ) : (
                        <>
                          <CommandEmpty>No matching SKU found.</CommandEmpty>
                          <CommandGroup heading="Products">
                            {skuOptions.map((option) => (
                              <CommandItem
                                key={`${option.id}-${option.sku}`}
                                value={`${option.sku} ${option.main_title ?? ''} ${option.id}`}
                                onSelect={() => {
                                  setSelectedProduct(option);
                                  setSkuQuery(option.sku);
                                  setSkuPickerOpen(false);
                                  setSaveError(null);
                                }}
                              >
                                <Check
                                  className={cn(
                                    'h-4 w-4',
                                    selectedProduct?.id === option.id ? 'opacity-100' : 'opacity-0'
                                  )}
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-mono text-sm">{option.sku}</div>
                                  <div className="truncate text-xs text-muted-foreground">
                                    {option.main_title || '(no title)'} | product_id: {option.id}
                                  </div>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                Start typing and choose a matching SKU from the list. Free-form SKU submission is disabled.
              </p>
              {selectedProduct && (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Badge variant="outline" className="font-normal">
                    product_id: {selectedProduct.id > 0 ? selectedProduct.id : '—'}
                  </Badge>
                  {selectedProduct.main_title && <Badge variant="outline" className="font-normal">{selectedProduct.main_title}</Badge>}
                </div>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="unit-cost">Unit cost</Label>
                <Input
                  id="unit-cost"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.0001"
                  placeholder="e.g. 4.6300"
                  value={unitCost}
                  onChange={(e) => setUnitCost(e.currentTarget.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Effective from</Label>
                <DatePickerButton value={effectiveFrom} onChange={setEffectiveFrom} placeholder="Pick start date" />
              </div>
              <div className="space-y-2">
                <Label>Effective to</Label>
                <DatePickerButton value={effectiveTo} onChange={setEffectiveTo} placeholder="Optional end date" />
              </div>
            </div>

            {effectiveTo && (
              <div className="flex justify-end">
                <Button type="button" variant="ghost" size="sm" onClick={() => setEffectiveTo('')}>
                  Clear end date
                </Button>
              </div>
            )}

            {saveError && (
              <div className="flex items-start gap-2 text-sm text-destructive">
                <AlertTriangle className="mt-0.5 h-4 w-4" />
                <span className="break-words">{saveError}</span>
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={saveRow} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete free gift cost row?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteRow ? (
                <>
                  This will permanently delete <span className="font-mono">{deleteRow.sku}</span> for row id{' '}
                  <span className="font-mono">{deleteRow.id}</span>.
                </>
              ) : (
                'This will permanently delete the selected free gift cost row.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {deleteError && (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4" />
              <span className="break-words">{deleteError}</span>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteSelectedRow} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
